import Button from 'components/Button';
import { useEffect } from 'react';
import { FieldErrors, useFormContext } from 'react-hook-form';

import { LP_INVESTMENT_LIST } from 'adminConstants/routes';
import FullHeightView from 'components/new/FullHeightView';
import PageLoader from 'components/PageLoader';
import { Vehicle } from 'further-types/lp/vehicle';
import { Row } from 'further-ui/components/Layout';
import { investment } from 'further-ui/validations';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import { useHistory, useParams } from 'react-router-dom';
import InputDetailsForm from './InputDetailsForm';

type StepCreateInvestmentProps = {
  onSuccess: () => void;
  selectedVehicle: Vehicle;
};

const StepCreateInvestment: React.FC<StepCreateInvestmentProps> = ({
  onSuccess,
  selectedVehicle,
}) => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { data: investment, isLoading } = api.lpInvestment.get.useQuery(
    {
      investmentId: id,
      vehicleId: selectedVehicle._id,
    },
    {
      enabled: !!id,
    },
  );

  const notification = useNotification();
  const { handleSubmit, setValue } = useFormContext();

  useEffect(() => {
    if (id && investment && !isLoading) {
      setValue('investmentId', investment.id);
      setValue('dealName', investment.dealName);
      setValue('assetId', investment.assetId);
      setValue('asset.name', investment.asset.name);
      setValue('isNewAsset', false);
      setValue('dealCompletionDate', investment.dealCompletionDate);
      setValue('fundingSource', investment.fundingSource);
      setValue(
        'holdingClasses',
        investment.holdingClasses.map((holdingClass) => ({
          ...holdingClass,
          isNewHoldingClass: false,
        })),
      );
      setValue('totalInvestmentAmountTcy', investment.totalInvestmentAmountTcy);
      setValue('units', investment.units);
      setValue('averageUnitPriceTcy', investment.averageUnitPriceTcy);
      setValue('vehicleId', investment.vehicleId);
      setValue('status', investment.status);
      setValue('allocationRule', investment.allocationRule);
      setValue('allocations', investment.allocations);
      setValue('transactions', investment.transactions);
      setValue('currencyRate', investment.currencyRate);
    }
  }, [investment, id, setValue, isLoading]);

  const onSubmit = () => {
    onSuccess();
  };

  const onError = (
    errors: FieldErrors<investment.CreateInvestmentFormValues>,
  ) => {
    const defaultError = 'Please fill in all required fields.';
    if (errors.holdingClasses) {
      notification.error(errors.holdingClasses.root?.message ?? defaultError);
    } else {
      notification.error(defaultError);
    }
  };

  const handleCancel = () => {
    history.push(LP_INVESTMENT_LIST);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <FullHeightView
      innerPaddingBottom="lg"
      actionBar={
        <Row justify="space-between" centered>
          <Button variant="outlined" onClick={handleCancel} size="large">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(onSubmit, onError)}
            size="large"
          >
            Continue
          </Button>
        </Row>
      }
    >
      <form>
        <InputDetailsForm selectedVehicle={selectedVehicle} />
      </form>
    </FullHeightView>
  );
};

export default StepCreateInvestment;
